import { Badge, BadgeProps, Icon, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiCheckSquare,
  FiClock,
  FiDollarSign,
  FiLock,
  FiPauseCircle,
  FiRefreshCw,
  FiShield,
  FiTruck,
  FiXCircle,
} from 'react-icons/fi'
import type { IconType } from 'react-icons'
import { normalizeOrderStatusCode } from '@/utils/statusUtils'

export type AdminStatusBadgeType =
  | 'order'
  | 'refund'
  | 'settlement'
  | 'registry'
  | 'merchant'
  | 'user'

interface StatusMeta {
  colorScheme: string
  icon?: IconType
  label?: string
  labelKey?: string
}

const ORDER_STATUS_META: Record<string, StatusMeta> = {
  awaiting_payment: {
    colorScheme: 'yellow',
    icon: FiClock,
    label: 'Awaiting Payment',
    labelKey: 'orders.status.awaitingPayment',
  },
  underpaid: {
    colorScheme: 'orange',
    icon: FiCheckCircle,
    label: 'Underpaid',
    labelKey: 'orders.status.underpaid',
  },
  fullpaid: {
    colorScheme: 'green',
    icon: FiCheckCircle,
    label: 'Full Paid',
    labelKey: 'orders.status.fullPaid',
  },
  overpaid: {
    colorScheme: 'green',
    icon: FiCheckCircle,
    label: 'Overpaid',
    labelKey: 'orders.status.overpaid',
  },
  settled: {
    colorScheme: 'blue',
    icon: FiCheckSquare,
    label: 'Settled',
    labelKey: 'orders.status.settled',
  },
  expired: {
    colorScheme: 'gray',
    icon: FiXCircle,
    label: 'Expired',
    labelKey: 'orders.status.expired',
  },
  full_refunded: {
    colorScheme: 'purple',
    icon: FiRefreshCw,
    label: 'Full Refunded',
    labelKey: 'orders.status.fullRefunded',
  },
  refunded: {
    colorScheme: 'purple',
    icon: FiRefreshCw,
    label: 'Refunded',
    labelKey: 'orders.status.refunded',
  },
  compliance_hold: {
    colorScheme: 'red',
    icon: FiShield,
    label: 'Compliance Hold',
    labelKey: 'orders.status.complianceHold',
  },
  closed: {
    colorScheme: 'gray',
    icon: FiLock,
    label: 'Closed',
    labelKey: 'orders.status.closed',
  },
  cancelled: {
    colorScheme: 'gray',
    icon: FiXCircle,
    label: 'Cancelled',
    labelKey: 'orders.status.cancelled',
  },
  payment_detected: {
    colorScheme: 'green',
    icon: FiDollarSign,
    label: 'Payment Detected',
    labelKey: 'orders.status.paymentDetected',
  },
  payment_confirmed: {
    colorScheme: 'green',
    icon: FiCheckCircle,
    label: 'Payment Confirmed',
    labelKey: 'orders.status.paymentConfirmed',
  },
  settlement_pending: {
    colorScheme: 'yellow',
    icon: FiTruck,
    label: 'Settlement Pending',
    labelKey: 'orders.status.settlementPending',
  },
  settlement_completed: {
    colorScheme: 'blue',
    icon: FiCheckSquare,
    label: 'Settlement Completed',
    labelKey: 'orders.status.settlementCompleted',
  },
}

const STATUS_META: Record<Exclude<AdminStatusBadgeType, 'order'>, Record<string, StatusMeta>> = {
  refund: {
    ros_pending: { colorScheme: 'yellow', icon: FiClock, label: 'Pending' },
    ros_refunded: { colorScheme: 'green', icon: FiCheckCircle, label: 'Refunded' },
    ros_failed: { colorScheme: 'red', icon: FiXCircle, label: 'Failed' },
  },
  settlement: {
    srs_pending: { colorScheme: 'yellow', icon: FiTruck, label: 'Pending' },
    srs_settled: { colorScheme: 'green', icon: FiCheckCircle, label: 'Settled' },
    srs_failed: { colorScheme: 'red', icon: FiXCircle, label: 'Failed' },
  },
  registry: {
    orrs_active: { colorScheme: 'yellow', icon: FiClock, label: 'Active' },
    orrs_on_hold: { colorScheme: 'orange', icon: FiPauseCircle, label: 'On Hold' },
    orrs_deployed: { colorScheme: 'green', icon: FiCheckCircle, label: 'Deployed' },
    orrs_cancelled: { colorScheme: 'gray', icon: FiXCircle, label: 'Cancelled' },
  },
  merchant: {
    mst_active: { colorScheme: 'green', icon: FiCheckCircle, label: 'Active' },
    mst_partial_active: { colorScheme: 'blue', icon: FiAlertTriangle, label: 'Partial Active' },
    mst_pending_config: { colorScheme: 'yellow', icon: FiClock, label: 'Pending Config' },
    mst_suspended: { colorScheme: 'red', icon: FiPauseCircle, label: 'Suspended' },
    mst_terminated: { colorScheme: 'gray', icon: FiXCircle, label: 'Terminated' },
  },
  user: {
    active: { colorScheme: 'green', icon: FiCheckCircle, label: 'Active' },
    inactive: { colorScheme: 'red', icon: FiXCircle, label: 'Inactive' },
    pending: { colorScheme: 'gray', icon: FiClock, label: 'Pending' },
  },
}

const normalizeStatus = (
  statusType: AdminStatusBadgeType,
  status?: string | boolean | null
): string => {
  if (statusType === 'user') {
    if (status === true) return 'active'
    if (status === false) return 'inactive'
  }

  const raw = String(status ?? '').trim()
  if (!raw) return statusType === 'user' ? 'pending' : 'unknown'

  if (statusType === 'order') {
    return normalizeOrderStatusCode(raw)
  }

  return raw.toLowerCase().replace(/-/g, '_')
}

export interface StatusBadgeProps extends Omit<BadgeProps, 'children'> {
  statusType: AdminStatusBadgeType
  status?: string | boolean | null
  label?: string
  showIcon?: boolean
}

export function StatusBadge({
  statusType,
  status,
  label,
  showIcon = true,
  variant = 'subtle',
  fontSize = 'xs',
  px = 1.5,
  py = 0.5,
  borderRadius = 'md',
  ...badgeProps
}: StatusBadgeProps) {
  const { t } = useTranslation()
  const normalized = normalizeStatus(statusType, status)
  const meta =
    statusType === 'order'
      ? ORDER_STATUS_META[normalized]
      : STATUS_META[statusType][normalized]
  const IconComponent = meta?.icon
  const resolvedLabel = label ?? (
    meta?.labelKey
      ? t(meta.labelKey, meta.label ?? normalized)
      : meta?.label ?? normalized
  )
  const shouldWrap = statusType === 'order'
    && (normalized === 'awaiting_payment' || normalized === 'compliance_hold')
  const displayLabel = shouldWrap ? resolvedLabel.replace(/\s+/u, '\n') : resolvedLabel

  return (
    <Badge
      colorScheme={meta?.colorScheme ?? 'gray'}
      variant={variant}
      fontSize={shouldWrap ? '10px' : fontSize}
      px={px}
      py={py}
      borderRadius={borderRadius}
      display="inline-flex"
      alignItems="center"
      gap={showIcon && IconComponent ? 1 : 0}
      lineHeight={shouldWrap ? '1.1' : undefined}
      {...badgeProps}
    >
      {showIcon && IconComponent && <Icon as={IconComponent} boxSize={3.5} flexShrink={0} />}
      <Text as="span" fontSize="inherit" lineHeight="inherit" whiteSpace={shouldWrap ? 'pre-line' : undefined}>
        {displayLabel}
      </Text>
    </Badge>
  )
}
